import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

/**
 * Reusable search-select / combobox for filters.
 *
 * Props:
 *  - value: currently selected value (string)
 *  - onChange: (newValue) => void
 *  - placeholder: shown when value is empty
 *  - specialOptions: [{ value, label }] shown above the search list (e.g. "me", "unassigned")
 *  - items: full list of items to filter
 *  - getKey, getLabel, getSecondary, matchesQuery: accessors on each item
 *  - groupLabel: label shown above the items section, e.g. "Agentes"
 *  - testId: prefix for data-testids
 *  - width: css width
 */
export default function SearchSelect({
    value,
    onChange,
    placeholder = "Seleccionar…",
    specialOptions = [],
    items = [],
    getKey = (it) => it.id,
    getLabel = (it) => it.label,
    getSecondary = () => null,
    matchesQuery = (it, q) => (it?.label || "").toLowerCase().includes(q),
    groupLabel = "Resultados",
    testId = "search-select",
    width = 220,
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const wrapRef = useRef(null);

    useEffect(() => {
        const onClickOut = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onClickOut);
        return () => document.removeEventListener("mousedown", onClickOut);
    }, []);

    const specialMatch = specialOptions.find((o) => o.value === value);
    const itemMatch = !specialMatch && value ? items.find((it) => getKey(it) === value) : null;
    const selectedLabel = !value
        ? ""
        : specialMatch
            ? specialMatch.label
            : (itemMatch ? getLabel(itemMatch) : "…");

    const q = query.toLowerCase();
    const filteredItems = q ? items.filter((it) => matchesQuery(it, q)) : items;

    const select = (v) => {
        onChange(v);
        setOpen(false);
        setQuery("");
    };

    return (
        <div ref={wrapRef} style={{ position: "relative", width }} data-testid={`${testId}-wrap`}>
            <input
                className="input"
                placeholder={placeholder}
                value={open ? query : selectedLabel}
                onFocus={() => { setOpen(true); setQuery(""); }}
                onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                data-testid={testId}
                style={{ paddingRight: value ? 30 : 12 }}
            />
            {value && (
                <button
                    type="button"
                    onClick={() => select("")}
                    data-testid={`${testId}-clear`}
                    title="Limpiar filtro"
                    style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", background: "transparent", border: 0, cursor: "pointer", color: "var(--text-muted)", display: "flex", alignItems: "center", padding: 4 }}
                >
                    <X size={14} />
                </button>
            )}
            {open && (
                <div
                    data-testid={`${testId}-dropdown`}
                    style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 30, maxHeight: 280, overflowY: "auto", background: "var(--bg-1)", border: "1px solid var(--border-strong)", borderRadius: 8, boxShadow: "0 12px 28px rgba(0,0,0,.4)" }}
                >
                    {specialOptions.map((opt) => (
                        <button
                            key={opt.value || "any"}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); select(opt.value); }}
                            style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", background: value === opt.value ? "var(--bg-2)" : "transparent", border: 0, color: "var(--text)", cursor: "pointer", fontSize: 13 }}
                            data-testid={`${testId}-opt-${opt.value || "any"}`}
                        >
                            {opt.label}
                        </button>
                    ))}
                    {specialOptions.length > 0 && (
                        <div style={{ borderTop: "1px solid var(--border)", margin: "4px 0" }} />
                    )}
                    <div style={{ padding: "4px 12px", fontSize: 10.5, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".06em" }}>
                        {groupLabel} ({filteredItems.length})
                    </div>
                    {filteredItems.length === 0 && (
                        <div style={{ padding: "8px 12px", color: "var(--text-muted)", fontSize: 12 }}>Sin coincidencias</div>
                    )}
                    {filteredItems.slice(0, 50).map((it) => {
                        const k = getKey(it);
                        const label = getLabel(it);
                        const secondary = getSecondary(it);
                        return (
                            <button
                                key={k}
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); select(k); }}
                                style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", background: value === k ? "var(--bg-2)" : "transparent", border: 0, color: "var(--text)", cursor: "pointer", fontSize: 13 }}
                                data-testid={`${testId}-opt-${k}`}
                            >
                                <div style={{ fontWeight: 600 }}>{label}</div>
                                {secondary && <div className="text-muted mono text-xs">{secondary}</div>}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
